import { wrappedFetch } from '@javascript/habitual-analytics/api/services/base';
import { REST_URLS } from '@javascript/habitual-analytics/api/resturls/index';
import {
  getApiCredentials,
  brokerSpecificTableHeaders
} from '@javascript/habitual-analytics/brokers/OrderFacade/utility';
import { Formatter } from '@javascript/habitual-analytics/brokers/OrderFacade/formatter/index';
import { Parser, TransactionResponse } from '@javascript/habitual-analytics/brokers/OrderFacade/parser/index';
import { TOASTIFY_TYPES, reactToastify } from '@javascript/globals/utils/reactToastify';
import { isArray } from 'lodash';
import { ErrorResult } from '@javascript/habitual-analytics/brokers/OrderFacade/types/common';
import _ from 'lodash';
import { TableField } from '@javascript/habitual-analytics/brokers/OrderFacade/types/insta';
import { TargetRestEndPoints } from '@javascript/habitual-analytics/brokers/OrderFacade/types/insta';
import { Facade } from '@javascript/habitual-analytics/brokers/OrderFacade/types/facade';
import { Insta } from '@javascript/habitual-analytics/brokers/OrderFacade/types/insta';

const apiCalls: TargetRestEndPoints = {
  placeOrders: 'placeOrders',
  cancelOrders: 'cancelOrders',
  modifyOrders: 'modifyOrders',
  orderBook: 'orderBook',
  positionBook: 'positionBook',
  tradeBook: 'tradeBook',
  holdingBook: 'holdingBook',
  marginCalculation: 'marginCalculation',
  postBackOrder: 'orderBookFromPostback',
};

type OrderDetails = Facade.PlaceOrderDetail
  | Facade.CancelOrderDetail
  | Facade.ModifyOrderDetail
  | Facade.MarginCalculationDetail

type ServerFailureResponse = {
  'message': string
  'code': string,
  'apiResponse'?: null
}

type ApiResponse = Facade.HoldingBook
  | Facade.OrderBook
  | Facade.PositionBook
  | Facade.TradeBook
  | Facade.Postback
  | Facade.PlaceOrderResult
  | Facade.CancelOrderResult
  | Facade.ModifyOrderResult
  | Facade.MarginCalculationResult


type Response = Facade.HoldingBookResult
  | Facade.OrderBookResult
  | Facade.PositionBookResult
  | Facade.TradeBookResult

interface TargetTableData {
  orderBookDetails: [Facade.OrderBook, Insta.OrderBook, Insta.OrderBookResult];
  tradeBookDetails: [Facade.TradeBook, Insta.TradeBook, Insta.TradeBookResult];
  positionBookDetails: [Facade.PositionBook, Insta.PositionBook, Insta.PositionBookResult];
  holdingBookDetails: [Facade.HoldingBook, Insta.HoldingBook, Insta.HoldingBookResult];
}

export type OrderFacadeService = {
  isValidAccessToken: () => Promise<boolean>;
  placeOrders: (orders: Insta.PlaceOrderDetail[])
    => Promise<(Insta.PlaceOrderResult | ErrorResult)[]>;
  cancelOrder: (order: Insta.CancelOrderDetail)
    => Promise<Insta.CancelOrderResult | ErrorResult>;
  modifyOrder: (order: Insta.ModifyOrderDetail[])
    => Promise<(Insta.ModifyOrderResult | ErrorResult)[]>;
  orderBook: () => Promise<Insta.OrderBookResult>;
  tradeBook: () => Promise<Insta.TradeBookResult>;
  positionBook: () => Promise<Insta.PositionBookResult>;
  holdingBook: () => Promise<Insta.HoldingBookResult>;
  marginCalculation: (orders: Insta.MarginCalculationDetail[])
    => Promise<Insta.MarginCalculationResult>;
  postBackOrder: (rawData: Insta.PostBackOrderDetail) =>
    Promise<Insta.Postback | undefined>;
};

export const OrderFacade = (broker: string): OrderFacadeService => {
  const BASE_URL = REST_URLS().ORDER_FACADE;

  const {
    orderTableHeaders,
    tradeTableHeaders,
    positionTableHeaders,
    holdingTableHeaders
  } = brokerSpecificTableHeaders();

  const parseBookResponse = <
    K extends keyof TargetTableData,
    In extends TargetTableData[K][0],
    Mid extends TargetTableData[K][1],
    Out extends TargetTableData[K][2]
  >(response: Response, headers: TableField[], rawBook: In[], resultKey: K, parser: (rawItem: In)
    => Mid | null): Out => {
    if (!response.success) {
      return {
        success: false,
        message: response.error?.reason || response.error?.message || 'Api Failed',
        headers,
        [resultKey]: []
      } as Out;
    }
    const parsedBook = isArray(rawBook)
      ? _.flatMap(rawBook, parser)
      : [parser(rawBook)];

    const sortKey = resultKey === 'positionBookDetails' ? 'extraDetails.isOpenPosition' : 'time';
    const sortedBooks = _.orderBy(_.compact(parsedBook), [sortKey], ['desc']) as Mid[];

    return {
      success: true,
      headers,
      [resultKey]: sortedBooks as Mid[]
    } as any as Out;
  };

  const makeApiCall = async (
    broker: string,
    method: string,
    orderDetails: OrderDetails[] | Insta.PostBackOrderDetail
  ): Promise<ApiResponse> => {
    const requestBody = {
      brokerName: broker?.toUpperCase(),
      apiMethodName: method,
      apiCredentials: getApiCredentials(broker),
      orders: orderDetails,
    };
    const parameters = {
      method: 'POST',
      body: JSON.stringify(requestBody),
    };
    const response = await wrappedFetch(`${BASE_URL}`, parameters);
    return response.json();
  };

  return {
    isValidAccessToken: async (): Promise<boolean> => {
      try {
        const response = await makeApiCall(broker, apiCalls.orderBook, []);
        const result = response as Facade.OrderBookResult;
        return result?.success || false;
      } catch (error: any) {
        console.log('error', error);
        return false;
      }
    },

    placeOrders: async (orders: Insta.PlaceOrderDetail[]):
      Promise<(Insta.PlaceOrderResult | ErrorResult)[]> => {
      const formattedOrders = _.map(orders, Formatter().placeOrder);
      try {
        const result = await makeApiCall(broker,
          apiCalls.placeOrders, formattedOrders) as TransactionResponse;
        const response = Parser().parseTransactionResponse(result);
        return _.map(response, (responseItem) => (responseItem.success)
          ? responseItem.orderId
          : responseItem
        );
      } catch (error: any) {
        const errorResponse: ServerFailureResponse = error;
        reactToastify(TOASTIFY_TYPES.ERROR, 'Place Orders: ' + errorResponse.message);
        throw errorResponse;
      }
    },

    cancelOrder: async (order: Insta.CancelOrderDetail):
      Promise<Insta.CancelOrderResult | ErrorResult> => {
      try {
        const result = await makeApiCall(broker, apiCalls.cancelOrders,
          Formatter().cancelOrder(order));
        const response = result as TransactionResponse;
        return {
          ...(Parser().parseTransactionResponse(response)[0]),
          orderId: order.orderNo
        };
      } catch (error: any) {
        const errorResponse: ServerFailureResponse = error;
        throw errorResponse;
      }
    },

    modifyOrder: async (order: Insta.ModifyOrderDetail[]):
      Promise<(Insta.ModifyOrderResult | ErrorResult)[]> => {
      try {
        const result = await makeApiCall(broker, apiCalls.modifyOrders,
          Formatter().modifyOrder(order[0])) as TransactionResponse;
        const response = Parser().parseTransactionResponse(result);
        return _.map(response, (responseItem) => (responseItem.success)
          ? responseItem.orderId
          : responseItem
        );
      } catch (error: any) {
        const errorResponse: ServerFailureResponse = error;
        reactToastify(TOASTIFY_TYPES.ERROR, 'Modify Order:' + errorResponse.message);
        throw errorResponse;
      }
    },

    orderBook: async (): Promise<Insta.OrderBookResult> => {
      try {
        const response = await makeApiCall(broker, apiCalls.orderBook, []);
        const result = response as Facade.OrderBookResult;

        return parseBookResponse(
          result,
          orderTableHeaders,
          _.get(result, 'orderBook', []),
          'orderBookDetails',
          Parser().parseOrderBook
        );
      } catch (error: any) {
        const errorResponse: ServerFailureResponse = error;
        throw errorResponse;
      }
    },

    tradeBook: async (): Promise<Insta.TradeBookResult> => {
      try {
        const response = await makeApiCall(broker, apiCalls.tradeBook, []);
        const result = response as Facade.TradeBookResult;

        return parseBookResponse(
          result,
          tradeTableHeaders,
          _.get(result, 'tradeBook', []),
          'tradeBookDetails',
          Parser().parseTradeBook
        );
      } catch (error: any) {
        const errorResponse: ServerFailureResponse = error;
        throw errorResponse;
      }
    },

    positionBook: async (): Promise<Insta.PositionBookResult> => {
      try {
        const response = await makeApiCall(broker, apiCalls.positionBook, []);
        const result = response as Facade.PositionBookResult;

        return parseBookResponse(
          result,
          positionTableHeaders,
          _.get(result, 'positionBook', []),
          'positionBookDetails',
          Parser().parsePositionBook
        );
      } catch (error: any) {
        const errorResponse: ServerFailureResponse = error;
        throw errorResponse;
      }
    },

    holdingBook: async (): Promise<Insta.HoldingBookResult> => {
      try {
        const response = await makeApiCall(broker, apiCalls.holdingBook, []);
        const result = response as Facade.HoldingBookResult;

        return parseBookResponse(
          result,
          holdingTableHeaders,
          _.get(result, 'holdingBook', []),
          'holdingBookDetails',
          Parser().parseHoldingBook
        );
      } catch (error: any) {
        const errorResponse: ServerFailureResponse = error;
        throw errorResponse;
      }
    },

    marginCalculation: async (orders: Insta.MarginCalculationDetail[]):
      Promise<Insta.MarginCalculationResult> => {
      try {
        const response = await makeApiCall(broker, apiCalls.marginCalculation,
          _.map(orders, Formatter().marginCalculation)
        ) as Facade.MarginCalculationResult;
        if (!response.success) {
          return {
            success: false,
            message: response.error?.reason || response.error?.message || 'Margin Calculation Failed'
          };
        }
        return {
          ..._.get(response, 'margin', {
            totalMarginAvailable: 0,
            totalMarginRequired: 0
          }), success: true
        };
      } catch (error: any) {
        const errorResponse: ServerFailureResponse = error;
        throw errorResponse;
      }
    },

    postBackOrder: async (rawData: Insta.PostBackOrderDetail):
      Promise<Insta.Postback> => {
      try {
        const result = await makeApiCall(
          broker, apiCalls.postBackOrder, rawData
        );
        const response = result as Facade.PostbackResult;
        return Parser().parsePostBackOrder(response?.orderBook[0]);
      } catch (error: any) {
        const errorResponse: ServerFailureResponse = error;
        throw errorResponse;
      }
    },
  };
};




